// Kit content data shared between the STEAM landing page and per-kit detail pages.
// Order here also drives the "prev / next kit" navigation on detail pages.

export const KITS = [
  {
    slug: "earth-day-native-tree",
    productSlug: "steam-kit-earth-day-native-tree",
    order: 1,
    tag: "Ecology & Botanical Art",
    tint: "#DCE842",
    tintText: "black",
    title: "Earth Day Native Tree & Forest Canopy",
    tagline: "Plant the seeds of environmental leadership.",
    ageRange: "Grades 3-8",
    duration: "6-8 hours across 4 sessions",
    intro:
      "A hands-on ecology and botanical-art expedition that turns any classroom, kitchen, or library into a working field station. Learners study soil, seeds, sunlight, and forest canopy structure the way real ecologists do, and finish with a technical botanical illustration worthy of the journals of George Washington Carver.",
    trailblazers: [
      {
        name: "Dr. Wangari Maathai",
        credit: "Kenyan biologist, environmental activist, and Nobel Peace Prize laureate.",
      },
      {
        name: "George Washington Carver",
        credit: "American agricultural scientist and inventor whose crop research reshaped Southern farming.",
      },
    ],
    activities: [
      { title: "Soil pH & Nutrient Lab", body: "Test three soil samples for pH, nitrogen, and moisture. Chart the results and pick the healthiest site for your seed." },
      { title: "Native Seed Germination Station", body: "Sprout regional native seeds in a mini greenhouse and log daily growth in a scientific journal." },
      { title: "Build an Elevation Clinometer", body: "Assemble a working paper-and-string clinometer to measure tree height and canopy angles like a real forester." },
      { title: "Botanical Field Illustration", body: "Sketch a leaf, seed, and root system in graphite and ink using the same technical style Carver used in his notebooks." },
    ],
    materials: [
      "3 soil sample tubes with pH strips and nutrient reagents",
      "6 native regional wildflower and tree seed packets",
      "Peat pellet greenhouse tray with humidity dome",
      "Precision graphite pencils (2H, HB, 2B) and ink liner",
      "100 gsm cotton sketch paper (10 sheets)",
      "Elevation clinometer kit (protractor, string, weight, straw)",
      "Waterproof field journal with prompts",
      "Facilitator guide with 4 session plans",
    ],
    standards: [
      { code: "NGSS 3-LS4-3", text: "Construct an argument that some organisms can survive well, some less well, in particular habitats." },
      { code: "NGSS 5-ESS3-1", text: "Obtain and combine information about ways individual communities protect Earth's resources and environment." },
      { code: "NGSS MS-LS2-4", text: "Construct an argument supported by evidence that changes to ecosystems affect populations." },
      { code: "CCSS.ELA-LITERACY.W.3-8", text: "Write informative texts to examine a topic and convey ideas clearly." },
    ],
    outcomes: [
      "Interpret soil test data and select a viable growing site.",
      "Diagram the germination cycle of a native plant.",
      "Use trigonometry (or ratio reasoning) to measure canopy height.",
      "Produce a labelled scientific botanical drawing.",
    ],
    photos: [
      { src: "/steam/school-visit.jpg", label: "In the classroom" },
      { src: "/steam/kit-inside.jpg", label: "What's inside the kit" },
      { src: "/steam/packing.jpg", label: "Kits packed for shipping" },
      { src: "/steam/kits-lineup.jpg", label: "Ready to deploy" },
    ],
  },
  {
    slug: "rhythms-and-resonance",
    productSlug: "steam-kit-rhythms-and-resonance",
    order: 2,
    tag: "Acoustics & Sound Engineering",
    tint: "#EC008C",
    tintText: "white",
    title: "Rhythms & Resonance",
    tagline: "Discover the physics of sound through custom music.",
    ageRange: "Grades 4-8",
    duration: "5-7 hours across 3 sessions",
    intro:
      "A tactile crash course in acoustics, wave physics, and instrument design. Students investigate how sound actually moves through air and wood, then assemble a wooden resonator box and tune their very own 5-key kalimba, an instrument they get to keep and take home.",
    trailblazers: [
      {
        name: "Dr. James E. West",
        credit: "American acoustician and co-inventor of the electret microphone found in most modern phones.",
      },
    ],
    activities: [
      { title: "Frequency & Wavelength Lab", body: "Use a tuning fork and cup of water to visualize sound waves and measure their frequency." },
      { title: "Resonator Box Assembly", body: "Sand, glue, and clamp the wooden resonator that will amplify the kalimba's tone." },
      { title: "5-Key Kalimba Build & Tune", body: "Cut steel tines to length using a chromatic tuner, mount them, and dial in a pentatonic scale." },
      { title: "Compose a Two-Bar Riff", body: "Notate an original rhythm on staff paper and perform it for the group." },
    ],
    materials: [
      "Pre-cut hardwood resonator panels (top, bottom, 4 sides)",
      "Set of 5 spring-steel kalimba tines with bridge and cross-bar",
      "Wood glue, sandpaper (120 / 220 grit) and clamp",
      "Digital chromatic tuner",
      "Tuning fork (A-440) and waveform observation cup",
      "Staff paper booklet and pencil",
      "Facilitator guide with tuning charts",
    ],
    standards: [
      { code: "NGSS 1-PS4-1", text: "Plan and conduct investigations to provide evidence that vibrating materials can make sound." },
      { code: "NGSS 4-PS4-1", text: "Develop a model of waves to describe patterns in terms of amplitude and wavelength." },
      { code: "NGSS MS-PS4-1", text: "Use mathematical representations to describe a simple model for waves." },
      { code: "CCSS.MATH.4.MD", text: "Solve problems involving measurement and conversion of measurements." },
    ],
    outcomes: [
      "Explain how frequency, amplitude, and wavelength relate.",
      "Assemble a resonating chamber that measurably amplifies pitch.",
      "Tune tuned percussion instruments using a chromatic reference.",
      "Notate and perform an original rhythmic composition.",
    ],
    photos: [
      { src: "/steam/kit-inside.jpg", label: "Inside a Rhythms kit" },
      { src: "/steam/school-visit.jpg", label: "Live at MLK Jr. Elementary" },
      { src: "/steam/kits-lineup.jpg", label: "Assembly line" },
      { src: "/steam/packing.jpg", label: "Kits packed for shipping" },
    ],
  },
  {
    slug: "urban-architects-bridges",
    productSlug: "steam-kit-urban-architects-bridges",
    order: 3,
    tag: "Structural Engineering",
    tint: "#F3E30A",
    tintText: "black",
    title: "Urban Architects & Structural Bridges",
    tagline: "Design the cities of tomorrow.",
    ageRange: "Grades 4-8",
    duration: "6-8 hours across 4 sessions",
    intro:
      "Students step into the shoes of a structural engineer and licensed architect. They draft scale blueprints, calculate load ratings, and construct working truss and suspension bridges. The final session is a live stress-test with real data collection: how much load did your bridge hold before it buckled?",
    trailblazers: [
      {
        name: "Norma Sklarek",
        credit: "The first Black woman licensed as an architect in New York and California, co-designer of the Pacific Design Center.",
      },
    ],
    activities: [
      { title: "Load Capacity & Tension Lab", body: "Suspend weights from paper strips of different widths and graph the failure point." },
      { title: "Scale Blueprint Drafting", body: "Draw a top-down and side-elevation plan of a bridge using ruler, T-square, and 1:20 scale." },
      { title: "Truss & Suspension Build", body: "Assemble two rival bridge designs from wood dowels, cardstock, and cotton cord." },
      { title: "Live Stress Test", body: "Load each bridge with graduated weights, record the failure load, and compare to the class average." },
    ],
    materials: [
      "40 birch dowels (assorted lengths)",
      "Precision cardstock deck sheets (10)",
      "Cotton engineering cord for suspension cables",
      "PVA construction glue with fine-tip applicator",
      "Ruler, T-square, and 1:20 scale template",
      "Set of graduated test weights (25g - 500g)",
      "Blueprint drafting booklet",
      "Facilitator guide with 4 session plans",
    ],
    standards: [
      { code: "NGSS 3-5-ETS1-1", text: "Define a simple design problem reflecting a need or want that includes specified criteria and constraints." },
      { code: "NGSS MS-ETS1-2", text: "Evaluate competing design solutions using a systematic process to determine how well they meet criteria." },
      { code: "CCSS.MATH.5.MD", text: "Convert like measurement units within a given measurement system." },
      { code: "CCSS.MATH.6.RP", text: "Understand ratio concepts and use ratio reasoning to solve problems." },
    ],
    outcomes: [
      "Compare tension vs compression in structural members.",
      "Produce a scale blueprint with two accurate views.",
      "Construct a bridge that meets a target load requirement.",
      "Record and analyze failure data across competing designs.",
    ],
    photos: [
      { src: "/steam/kits-lineup.jpg", label: "Fresh from the workshop" },
      { src: "/steam/kit-inside.jpg", label: "Blueprints and dowels" },
      { src: "/steam/school-visit.jpg", label: "In the classroom" },
      { src: "/steam/packing.jpg", label: "Made to order" },
    ],
  },
  {
    slug: "solar-trackers-sculptures",
    productSlug: "steam-kit-solar-trackers-sculptures",
    order: 4,
    tag: "Renewable Energy",
    tint: "#DCE842",
    tintText: "black",
    title: "Solar Trackers & Kinetic Sculptures",
    tagline: "Harvest light to create motion.",
    ageRange: "Grades 4-8",
    duration: "5-7 hours across 3 sessions",
    intro:
      "A renewable-energy studio that fuses low-voltage electronics with kinetic sculpture. Students wire real solar cells, build a gear-driven sun-tracking arm that follows the light across a windowsill, and then design a light-refracting whirligig that spins entirely from harvested sunlight.",
    trailblazers: [
      {
        name: "Lonnie Johnson",
        credit: "Aerospace engineer, inventor of the Super Soaker, and a leading researcher in solid-state energy conversion.",
      },
    ],
    activities: [
      { title: "Solar Cell Circuit Build", body: "Wire a small photovoltaic cell to a motor and confirm output with a multimeter." },
      { title: "Sun-Tracker Gear Assembly", body: "Snap together a 3-gear reduction that turns a paddle toward the brightest light source." },
      { title: "Kinetic Whirligig Design", body: "Sketch and cut a mylar whirligig, then mount it on the tracker for reflected light patterns." },
      { title: "Energy Output Log", body: "Record voltage under three lighting conditions and graph the results." },
    ],
    materials: [
      "1V / 200mA polycrystalline solar cell",
      "3V hobby motor with propeller and paddle attachments",
      "Modular gear set (12T, 24T, 36T)",
      "Snap-lead alligator clip wire pack",
      "Multimeter with data-recording sheet",
      "Mylar reflective sheet and whirligig template",
      "Kraft base plate with pre-drilled mounts",
      "Facilitator guide with 3 session plans",
    ],
    standards: [
      { code: "NGSS 4-PS3-2", text: "Make observations to provide evidence that energy can be transferred by sound, light, heat, and electric currents." },
      { code: "NGSS 5-PS3-1", text: "Use models to describe that energy in animals' food was once energy from the sun." },
      { code: "NGSS MS-PS3-3", text: "Apply scientific principles to design, construct, and test a device that minimizes or maximizes thermal energy transfer." },
      { code: "CCSS.MATH.5.MD", text: "Represent and interpret data using line plots and bar graphs." },
    ],
    outcomes: [
      "Wire a functioning photovoltaic circuit.",
      "Explain gear ratios and mechanical advantage.",
      "Compare voltage output across lighting conditions.",
      "Design a kinetic sculpture powered entirely by sunlight.",
    ],
    photos: [
      { src: "/steam/packing.jpg", label: "Assembly day" },
      { src: "/steam/kits-lineup.jpg", label: "Made to order" },
      { src: "/steam/school-visit.jpg", label: "At MLK Jr. Elementary" },
      { src: "/steam/kit-inside.jpg", label: "Kit contents" },
    ],
  },
];

export const getKit = (slug) => KITS.find((k) => k.slug === slug);
